/**
 * Admin → SMS Diagnostics
 * Shows recent SMS attempts with LIVE Telnyx delivery status so you can
 * instantly see whether messages are actually reaching handsets or being
 * silently dropped (typical A2P 10DLC unregistered symptom).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Loader2, MessageSquare, AlertCircle, CheckCircle2, XCircle, Settings } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function statusPill(status) {
  const s = (status || '').toLowerCase();
  if (s === 'delivered')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"><CheckCircle2 className="h-3 w-3"/>Delivered</span>;
  if (s === 'sending_failed' || s === 'delivery_failed' || s === 'failed')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200"><XCircle className="h-3 w-3"/>{status}</span>;
  if (s === 'sent')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"><AlertCircle className="h-3 w-3"/>Sent (unconfirmed)</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200">{status || 'unknown'}</span>;
}

export default function AdminSmsLogsPanel({ adminToken }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [checkingConfig, setCheckingConfig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/sms/logs?limit=25`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setLogs(j.logs || []);
    } catch (e) {
      console.error('sms logs load failed', e);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  const checkConfig = useCallback(async () => {
    setCheckingConfig(true);
    try {
      const r = await fetch(`${API}/api/admin/sms/config-check`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const j = await r.json();
      setConfig(j);
    } catch (e) {
      setConfig({ error: e.message });
    } finally {
      setCheckingConfig(false);
    }
  }, [adminToken]);

  useEffect(() => { load(); checkConfig(); }, [load, checkConfig]);

  const [connecting, setConnecting] = useState(false);
  const [connectMsg, setConnectMsg] = useState(null);
  const connectReplies = async () => {
    setConnecting(true);
    setConnectMsg(null);
    try {
      const r = await fetch(`${API}/api/admin/sms/connect-replies`, { method: 'POST', headers: { Authorization: `Bearer ${adminToken}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setConnectMsg({ ok: true, text: `Connected. Patient replies now go to ${j.webhook_url}${j.previous_webhook_url ? ` (previously ${j.previous_webhook_url})` : ''}.` });
      checkConfig();
    } catch (e) {
      setConnectMsg({ ok: false, text: e.message });
    } finally {
      setConnecting(false);
    }
  };

  const [lookupId, setLookupId] = useState('');
  const [lookup, setLookup] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const doLookup = async () => {
    if (!lookupId.trim()) return;
    setLookingUp(true);
    try {
      const r = await fetch(`${API}/api/admin/sms/lookup/${encodeURIComponent(lookupId.trim())}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setLookup(await r.json());
    } catch (e) {
      setLookup({ error: e.message });
    } finally {
      setLookingUp(false);
    }
  };

  // Detect the 10DLC symptom pattern
  const has10DLCIssue = logs.length > 0 && logs.slice(0, 5).every(
    l => l.live?.to_status === 'sent' || l.diagnosis?.includes('10DLC') || l.diagnosis?.includes('sent')
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">SMS Diagnostics</h2>
          <p className="text-sm text-slate-600">Live Telnyx delivery status for recent messages.</p>
        </div>
        <button
          onClick={() => { load(); checkConfig(); }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          data-testid="sms-logs-refresh"
        >
          <RefreshCw className="h-4 w-4" /> Refresh live status
        </button>
      </div>

      <div className={`border rounded-lg p-4 space-y-2 ${config?.replies_connected ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`} data-testid="sms-replies-panel">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm font-semibold text-slate-900">Patient replies</div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config?.replies_connected ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`} data-testid="sms-replies-status">
            {config?.replies_connected ? 'Connected' : 'Not connected'}
          </span>
          {!config?.replies_connected && (
            <button onClick={connectReplies} disabled={connecting} className="ml-auto px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="sms-connect-replies-btn">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect replies to PractCom'}
            </button>
          )}
        </div>
        <div className="text-xs text-slate-600">
          {config?.replies_connected
            ? 'Texts patients send back appear in Messages, and the office can reply from there.'
            : `Telnyx is not sending patient replies to PractCom${config?.inbound_webhook_url ? ` (currently: ${config.inbound_webhook_url})` : ''}. One click fixes it.`}
        </div>
        {connectMsg && <div className={`text-sm ${connectMsg.ok ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="sms-connect-replies-result">{connectMsg.text}</div>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="sms-lookup-panel">
        <div className="text-sm font-semibold text-slate-900">Look up a text by message ID</div>
        <div className="flex gap-2">
          <input
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doLookup()}
            placeholder="e.g. 4031a14f-f866-43b6-abdc-5fd45eb57d40"
            className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-sm font-mono"
            data-testid="sms-lookup-input"
          />
          <button onClick={doLookup} disabled={lookingUp} className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm disabled:opacity-60" data-testid="sms-lookup-btn">
            {lookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check delivery'}
          </button>
        </div>
        {lookup && (
          <div className="text-sm space-y-1" data-testid="sms-lookup-result">
            {lookup.error ? (
              <div className="text-rose-700">{lookup.error}</div>
            ) : (
              <>
                <div className="flex items-center gap-2">Status: {statusPill(lookup.live?.to_status)}</div>
                <div className="text-slate-700">{lookup.diagnosis}</div>
                {lookup.live?.errors?.length > 0 && (
                  <pre className="text-xs bg-slate-50 border rounded p-2 whitespace-pre-wrap">{JSON.stringify(lookup.live.errors, null, 2)}</pre>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* 10DLC warning banner */}
      {has10DLCIssue && (
        <div className="bg-rose-50 border-l-4 border-rose-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-rose-900">A2P 10DLC registration likely required</div>
              <div className="text-rose-800 mt-1">
                Recent messages show status "sent" but are not confirming as "delivered". Since 2023, US carriers (AT&amp;T, Verizon, T-Mobile) require Telnyx phone numbers to be registered under A2P 10DLC. Without registration, messages are silently dropped even though Telnyx accepts them.
              </div>
              <div className="mt-2 text-xs">
                <a
                  href="https://portal.telnyx.com/#/app/messaging/10dlc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-rose-700 font-semibold underline"
                >
                  Open Telnyx 10DLC portal →
                </a>
                &nbsp; Register your Brand ($4 one-time) + Campaign to fix.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Config card */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Settings className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">Telnyx Configuration</h3>
          {checkingConfig && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
        </div>
        {config ? (
          <>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <ConfigRow label="API Key" ok={config.api_key_set} value={config.api_key_preview || '—'} />
              <ConfigRow label="From Number" ok={config.from_number_set} value={config.from_number || '—'} />
              <ConfigRow label="Messaging Profile" ok={config.messaging_profile_set} value={config.messaging_profile_id || '—'} />
              <ConfigRow label="Telnyx Reachable" ok={config.telnyx_reachable} value={config.telnyx_reachable ? '✓' : (config.telnyx_error || '—')} />
              {config.messaging_profile_set && (
                <>
                  <ConfigRow label="Profile Found in Telnyx" ok={config.configured_profile_found} value={config.profile_name || '—'} />
                  <ConfigRow
                    label="From-Number Attached to Profile"
                    ok={config.from_number_is_attached_to_profile}
                    value={config.from_number_is_attached_to_profile ? '✓ Yes' : '✗ NO — this is the bug'}
                  />
                </>
              )}
            </div>

            {/* THE DEFINITIVE DIAGNOSIS */}
            {config.profile_phone_numbers && config.from_number_is_attached_to_profile === false && (
              <div className="mt-4 bg-rose-50 border-l-4 border-rose-500 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-bold text-rose-900">🎯 Root cause confirmed: wrong phone number configured</div>
                    <div className="text-rose-800 mt-1.5">
                      PractCom is trying to send from <code className="bg-white/70 px-1.5 rounded text-rose-900 font-mono">{config.from_number}</code>, but that number is <strong>NOT attached</strong> to the Telnyx profile <code className="bg-white/70 px-1.5 rounded text-rose-900">{config.profile_name}</code>. The numbers that ARE attached (and 10DLC-registered via your approved campaign):
                    </div>
                    <ul className="mt-2 ml-5 list-disc text-rose-900 font-mono text-sm">
                      {config.profile_phone_numbers.map(n => <li key={n}>{n}</li>)}
                    </ul>
                    <div className="mt-3 bg-white/70 rounded-lg p-3 border border-rose-200">
                      <div className="font-semibold text-rose-900 mb-1">Fix (60 seconds, zero risk to MMB AI):</div>
                      <ol className="list-decimal ml-5 space-y-1 text-rose-800">
                        <li>Open Emergent → PractCom app → ⚙️ Secrets</li>
                        <li>Change <code className="bg-white px-1 rounded">TELNYX_PHONE_NUMBER</code> to <code className="bg-white px-1 rounded font-bold">{config.profile_phone_numbers[0]}</code></li>
                        <li>Save → restart backend → send yourself a test SMS</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {config.profile_phone_numbers && config.from_number_is_attached_to_profile === true && (
              <div className="mt-4 bg-emerald-50 border-l-4 border-emerald-500 rounded-lg p-3 text-sm text-emerald-900">
                ✅ From-number is correctly attached to the messaging profile. 10DLC should be covered — if SMS still fails, check the Logs below for carrier-specific errors.
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-slate-500">Loading config…</div>
        )}
      </div>

      {/* Logs */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading logs…
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white rounded-xl border border-dashed">
          <MessageSquare className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No SMS messages have been sent yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wide">
            <div className="col-span-2">To</div>
            <div className="col-span-3">Message</div>
            <div className="col-span-2">Live status</div>
            <div className="col-span-3">Diagnosis</div>
            <div className="col-span-2">Sent at</div>
          </div>
          <div className="divide-y divide-slate-100">
            {logs.map(l => (
              <div key={l.id} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm items-start" data-testid={`sms-log-${l.id}`}>
                <div className="col-span-2 font-mono text-xs">{l.to}</div>
                <div className="col-span-3 text-slate-600 truncate">{l.message}</div>
                <div className="col-span-2">{statusPill(l.live?.to_status || l.status_at_send)}</div>
                <div className="col-span-3 text-xs text-slate-700">{l.diagnosis}</div>
                <div className="col-span-2 text-xs text-slate-500">
                  {l.sent_at ? new Date(l.sent_at).toLocaleString() : '—'}
                </div>
                {l.live?.errors?.length > 0 && (
                  <div className="col-span-12 -mt-1 ml-[16.67%] text-xs text-rose-700 bg-rose-50 rounded p-2 border border-rose-100">
                    <strong>Telnyx errors:</strong> {l.live.errors.map(e => `${e.title || ''}${e.detail ? ': '+e.detail : ''}`).join('; ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigRow({ label, ok, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="flex items-center gap-2">
        {ok === true && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
        {ok === false && <XCircle className="h-3.5 w-3.5 text-rose-500" />}
        <span className="font-mono text-xs text-slate-800">{value}</span>
      </span>
    </div>
  );
}
